import React, { useState } from "react";
import { IInvoice, invoiceSchema } from "../../interfaces/IInvoice";
import { IInvoiceState } from "../../interfaces/IInvoiceState";
import { IInvoiceType } from "../../interfaces/IInvoiceType";
import { IRecipientOfPayment } from "../../interfaces/IRecipientOfPayment";
import { CircularProgress, Collapse, Dialog, DialogContent, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import { DataGrid, deDE, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useGetProjectSmall } from "../../hooks/data/data_project";
import { Delete, Download, Edit, Link, Search } from "@mui/icons-material";
import { InvoiceStateChip } from "./InvoiceStateChip";
import { ICustomer } from "../../interfaces/ICustomer";
import { IFeedback } from "../../interfaces/IFeedback";
import { useDeleteInvoice, useGenerateInvoiceDocument, useGenerateInvoiceDocumentByFunction, useGetInvoiceStates, useGetInvoiceTypes } from "../../hooks/data/data_invoice";
import { InvoiceEditDialog } from "./InvoiceEditDialog";
import { IDocument } from "../../interfaces/IDocument";
import { ErrorTryRefetch } from "../core/ErrorTryRefetch";
import { CustomSnackbar } from "../core/Snackbar";
import { CustomeTextField } from "../core/CustomeTextField";


interface IProps {
    invoices: IInvoice[];
    invoiceStates: IInvoiceState[];
    invoiceTypes: IInvoiceType[];
    bankAccounts: IRecipientOfPayment[];
    refetechInvoices: Function;
    isFetchingInvoices: boolean;
}


interface IFilterObject {
    idState: number;
    idType: number;
    from_date: string | null;
    to_date: string | null;
    searchTerm: string;
}


export const InvoicesOverview:React.FC<IProps> = ({
    invoices,
    invoiceStates,
    invoiceTypes,
    bankAccounts,
    refetechInvoices,
    isFetchingInvoices
}) => {
    const { projectSmallArray, isLoadingProjectSmallArray, refetchProjectSmall } = useGetProjectSmall();
    const { getInoiceDocument, isPendingInvoiceDocument } = useGenerateInvoiceDocumentByFunction();
    const {deleteInvoice,isPendingDeleteInvoice} = useDeleteInvoice();
    //
    const [filter,setFilter] = useState<IFilterObject>({idState: -1, idType: 0, from_date: null, to_date: null,searchTerm:""});
    //
    const [feedback, setFeedback] = useState<IFeedback>({ open: false, message: "", severity: "info" });
    const [isOpenEdit,setIsOpenEdit] = useState(false);
    const [editInvoice,setEditInvoice] = useState<IInvoice>(invoiceSchema.parse({Project: -1}));
    //
    const [isOpenShowDocument,setIsOpenShowDocument] = useState(false);
    const [invDocument,setInvDocument] = useState<IDocument|null>(null);
    const [showLoadingDownload,setShowLoadingDownload] = useState(false);


    const filterSearchTerm = (inv:IInvoice) => {
        let project = projectSmallArray?.find(proj => proj.id === inv.Project)
        
        return(
            (inv.title.toLocaleLowerCase().includes(filter.searchTerm.toLocaleLowerCase()))
            || (
                    inv.Customer !== null && (
                        filter.searchTerm.toLocaleLowerCase().includes(inv.Customer?.firstname.toLocaleLowerCase())
                        || inv.Customer?.firstname.toLocaleLowerCase().includes(filter.searchTerm.toLocaleLowerCase())
                    )
                )
            || (
                    inv.Customer !== null && (
                        filter.searchTerm.toLocaleLowerCase().includes(inv.Customer?.lastname.toLocaleLowerCase())
                        || inv.Customer?.lastname.toLocaleLowerCase().includes(filter.searchTerm.toLocaleLowerCase())
                    )
                )
            || (
                    project !== undefined && (
                        filter.searchTerm.toLocaleLowerCase().includes(project.title.toLocaleLowerCase())
                        || project.title.toLocaleLowerCase().includes(filter.searchTerm.toLocaleLowerCase())
                    )
                )

        )    
    }

    const filteredInvoices = invoices
        .filter(inv => filter.idState === 0 || ( filter.idState === -1 && (inv.InvoiceState < 50 || inv.InvoiceState >= 70)) ||  inv.InvoiceState === filter.idState)
        .filter(inv => filter.idType === 0 || inv.InvoiceType === filter.idType)
        .filter(inv => filter.from_date === null || new Date(String(inv.invoice_send_at)) > new Date(String(filter.from_date)) )
        .filter(inv => filter.to_date === null || new Date(String(inv.invoice_send_at)) > new Date(String(filter.to_date)) )
        .filter(inv => filter.searchTerm === "" || filterSearchTerm(inv) )
    ;


    const handleRemove = async (inv:IInvoice) => {
        try {
            await deleteInvoice(inv.id);
            setFeedback({
                open: true,
                message: "Erfolgreich gelöscht!",
                severity: "success"
            })
            refetechInvoices();
        } catch (error) {
            setFeedback({
                open: true,
                message: "Es ist ein Fehler aufgetretten!",
                severity: "error"
            })
        }
    }

    const downloadFile = (localDoc:IDocument) => {
        if (localDoc !== null && localDoc.data) {
            const blob = new Blob([Uint8Array.from(atob(localDoc.data), c => c.charCodeAt(0))], {
                type: 'application/pdf',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = localDoc.file_name || 'document.pdf';
            a.click();
            URL.revokeObjectURL(url);
        }
        setShowLoadingDownload(false);
    }

    const handleShowDocument = async (invoice:IInvoice,isPreview:boolean) => {
        if (isPreview) {
            setIsOpenShowDocument(true);
        } else {
            setShowLoadingDownload(true);
        }
        

        if (invoice.Document === null) {
            const result = await getInoiceDocument(invoice);
            setInvDocument(result)
            if (!isPreview) {
                downloadFile(result);
            }
        } else {
            setInvDocument(invoice.Document)
            if (!isPreview) {
                downloadFile(invoice.Document);
            }
        }
        
    }


    const columns: GridColDef<IInvoice>[] = [
        {
            field: "invoice_number",
            headerName: "RG-Nummer",
            width: 150,
        },
        {
            field: "InvoiceState",
            headerName: "Status",
            width: 150,
            renderCell: (params:  GridRenderCellParams<any, IInvoice>)  => 
                <InvoiceStateChip
                    invoice={params.row}
                    invoiceState={invoiceStates}
                />
        },
        {
            field: "Project",
            headerName: "Projekt",
            renderCell: (params:  GridRenderCellParams<any, IInvoice>)  => {
                if (isLoadingProjectSmallArray) {
                    return <CircularProgress/>
                }
                if (!projectSmallArray) {
                    return <>Konnte Projekte nicht beziehen!</>
                }
                return (
                    <>
                        {projectSmallArray.find(x => x.id === Number(params.value))?.title}
                        <IconButton
                            onClick={() => 
                                window.open(`projects/${params.value}`, '_blank')
                            }
                        >
                            <Link/>
                        </IconButton>
                    </>
                )
            },
            flex: 1
        },
        {
            field: "title",
            headerName: "Titel",
            flex: 1
        },
        {
            field: "Customer",
            headerName: "Kunde",
            flex: 1,
            renderCell: (params: GridRenderCellParams<any, IInvoice>) => {
                let customer:ICustomer|null = params.row.Customer;
                
                if (customer === null) {
                    return <>-</>
                } else {
                    return <>{customer.firstname} {customer.lastname}</>
                }
            }
            ,
        },
        {
            field: "invoice_send_at",
            headerName: "RG-Ausgang",
            renderCell: (params: GridRenderCellParams<any, IInvoice>) => 
                (params.value === null) ? "-" : new Date(String(params.value)).toLocaleDateString()
            ,
            
            width: 150,
        },
        {
            field: "total",
            headerName: "RG-Summe",
            renderCell: (params: GridRenderCellParams<any, IInvoice>) => 
                (Number(params.value)*(1+Number(params.row.vat))).toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })
            ,
            
            width: 150,
        },
        {
            field: "paid_at",
            headerName: "Geld-Eingang",
            renderCell: (params: GridRenderCellParams<any, IInvoice>) => 
                (params.value === null) ? "-" : new Date(String(params.value)).toLocaleDateString()
            ,
            
            width: 150,
        },
        {
            field: "Actions",
            headerName: "Aktionen",
            renderCell: (params: GridRenderCellParams<any, IInvoice>) => {
                const invoice = invoiceSchema.parse(params.row);
                return(
                    <>
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleShowDocument(invoice,true)
                            }}
                        >
                            <Search/>
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleShowDocument(invoice,false)
                            }}
                        >
                            <Download/>
                        </IconButton>
                        { (invoice.invoice_number === "") &&
                            <IconButton
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemove(invoice)
                                }}
                            >
                                <Delete/>
                            </IconButton>
                        }
                    </>
                )
            }


            ,
            
            width: 160,
        },
    ]

    return(
        <>
            <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />

            {(isPendingDeleteInvoice) &&
                <Dialog open>
                    <DialogContent>
                        Wird gelöscht...
                    </DialogContent>
                </Dialog>
            }
            {(showLoadingDownload && isPendingInvoiceDocument) &&
                <Dialog open>
                    <DialogContent>
                        Generiere Dokument(e)...
                    </DialogContent>
                </Dialog>
            }

            {(isOpenShowDocument) &&
                <Dialog
                    open
                    onClose={() => setIsOpenShowDocument(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogContent>
                        { (isPendingInvoiceDocument)
                            ? <CircularProgress/>
                            : (!invDocument) 
                            ? <ErrorTryRefetch refetchFunction={() => setIsOpenShowDocument(false)}/>
                            : <iframe 
                                src={`data:application/pdf;base64,${invDocument.data}`}
                                title={invDocument.file_name}
                                name={invDocument.file_name}
                            />
                        }
                    </DialogContent>
                </Dialog>
            }

            <InvoiceEditDialog
                key={`invoice-dialog-editor-${editInvoice.id}`}
                editInv={editInvoice}
                setEditInv={setEditInvoice}
                invoices={invoices}
                bankAccounts={bankAccounts}
                invTypes={invoiceTypes}
                invStates={invoiceStates}
                isOpenEdit={isOpenEdit}
                setIsOpenEdit={setIsOpenEdit}
                setFeetback={setFeedback}
                refetchFunction={() => refetechInvoices()}
            />

            <Typography
                variant="h6"
                sx={{
                    mt: 5,
                    mb: 2,
                    display: "flex"
                }}
            >
                Rechnungen
                <Collapse in={isFetchingInvoices}>
                    <CircularProgress size={30} sx={{ml: 1, mt: 0.5}}/>
                </Collapse>
            </Typography>

            <Grid container spacing={2} sx={{mb: 2}}>
                <Grid item xs={2}>
                    <CustomeTextField
                        attr="from_date"
                        label="Vom (RG-Ausgang)"
                        object={filter}
                        setObject={setFilter}
                        type="date"
                        null
                    />
                </Grid>
                <Grid item xs={2}>
                    <CustomeTextField
                        attr="to_date"
                        label="Bis (RG-Ausgang)"
                        object={filter}
                        setObject={setFilter}
                        type="date"
                        null
                    />
                </Grid>
                <Grid item xs={2}>
                    <CustomeTextField
                        attr="idType"
                        label="Art"
                        object={filter}
                        setObject={setFilter}
                        type="select"
                    >
                        {[
                            {"id": 0, "invoiceType" : "Alle"},
                            ...invoiceTypes
                        ].map(state =>
                            <MenuItem
                                key={`fitlter-state-${state.id}`}
                                value={state.id}
                            >
                                {state.invoiceType}
                            </MenuItem>
                        )}
                    </CustomeTextField>
                </Grid>
                <Grid item xs={2}>
                    <CustomeTextField
                        attr="idState"
                        label="Status"
                        object={filter}
                        setObject={setFilter}
                        type="select"
                    >
                        {[
                            {"id": 0, "invoiceState" : "Alle"},
                            {"id": -1, "invoiceState" : "Nur Offene"},
                            ...invoiceStates
                        ].map(state =>
                            <MenuItem
                                key={`fitlter-state-${state.id}`}
                                value={state.id}
                            >
                                {state.invoiceState}
                            </MenuItem>
                        )}
                    </CustomeTextField>
                </Grid>
                <Grid item xs={4}>
                    <CustomeTextField
                        attr="searchTerm"
                        label="Suche (Rechnungstitel, Projekttitel, Kunde)"
                        object={filter}
                        setObject={setFilter}
                        type="string"
                    />
                </Grid>

            </Grid>

            <DataGrid
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={filteredInvoices}
                columns={columns}
                //getRowId={(row) => row.id}
                onRowClick={(params, event, details) => {
                    setEditInvoice(invoiceSchema.parse(params.row));
                    setIsOpenEdit(true)
                }}
                autoHeight
            />
        </>
    )
}